import { getCollection } from './firestoreService';
import { convertTimestamp } from './firestoreService';
import { subDays, format } from 'date-fns';

export interface SubscriptionEvent {
  id: string;
  eventType: string;
  userId?: string;
  timestamp: Date;
  platform?: string;
  status?: string;
  planId?: string;
  properties?: Record<string, unknown>;
}

export interface SubscriptionLifecycleSummary {
  events: SubscriptionEvent[];
  byEventType: Record<string, number>;
  byPlatform: Record<string, number>;
  last7dCount: number;
  last30dCount: number;
  eventsOverTime: Array<{ date: string; count: number }>;
}

export const getSubscriptionLifecycleEvents = async (
  days: number = 30
): Promise<SubscriptionLifecycleSummary> => {
  const startDate = subDays(new Date(), days);

  const raw = await getCollection<any>(
    'analytics_subscription_events',
    [
      {
        field: 'timestamp',
        operator: '>=',
        value: startDate,
      },
    ],
    'timestamp',
    'desc',
    2000
  ).catch(() => []);

  const events: SubscriptionEvent[] = raw.map((e: any) => {
    const ts = e.timestamp?.toDate ? e.timestamp.toDate() : convertTimestamp(e.timestamp);
    return {
      id: e.id || '',
      eventType: e.eventType ?? e.event_type ?? 'unknown',
      userId: e.userId ?? e.user_id,
      timestamp: ts,
      platform: e.platform,
      status: e.status,
      planId: e.planId ?? e.plan_id,
      properties: e.properties ?? e.data,
    };
  });

  const byEventType: Record<string, number> = {};
  const byPlatform: Record<string, number> = {};
  const byDate = new Map<string, number>();

  events.forEach((e) => {
    const evType = String(e.eventType ?? 'unknown');
    byEventType[evType] = (byEventType[evType] || 0) + 1;
    const rawPlatform = e.platform ?? (e.properties && typeof (e.properties as Record<string, unknown>).platform === 'string' ? (e.properties as Record<string, string>).platform : undefined);
    const platform = rawPlatform && rawPlatform !== 'null' ? String(rawPlatform) : 'unknown';
    byPlatform[platform] = (byPlatform[platform] || 0) + 1;
    const dateKey = format(e.timestamp, 'yyyy-MM-dd');
    byDate.set(dateKey, (byDate.get(dateKey) || 0) + 1);
  });

  const now = new Date();
  const last7d = subDays(now, 7);
  const last7dCount = events.filter((e) => e.timestamp >= last7d).length;

  const eventsOverTime = Array.from(byDate.entries())
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    events,
    byEventType,
    byPlatform,
    last7dCount,
    last30dCount: events.length,
    eventsOverTime,
  };
};
